import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  MessageCircle, Send, Users, Search, ArrowLeft, 
  Loader2, User, Clock, CheckCheck, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const Messagerie = () => {
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadData();
    // Récupérer l'ID de l'utilisateur courant
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.sub);
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const loadData = async () => {
    try {
      const [convsRes, contactsRes] = await Promise.all([
        api.get('/messagerie/conversations'),
        api.get('/messagerie/contacts')
      ]);
      setConversations(convsRes.data || []);
      setContacts(contactsRes.data || []);
    } catch (error) {
      console.error('Erreur chargement messagerie:', error);
      toast.error('Erreur de chargement de la messagerie');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await api.get(`/messagerie/conversations/${conversationId}`);
      setMessages(response.data.messages || []);
      setSelectedConversation(response.data.conversation);
      // Mettre à jour le compteur de non-lus dans la liste
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? {...c, non_lus: 0} : c
      ));
    } catch (error) {
      console.error('Erreur chargement messages:', error);
      toast.error('Erreur de chargement des messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    // Trouver l'ID du destinataire (l'autre participant)
    const destinataireId = selectedConversation.participants.find(id => id !== currentUserId);
    if (!destinataireId) return;
    
    setSending(true);
    try {
      const response = await api.post('/messagerie/messages', {
        destinataire_id: destinataireId,
        contenu: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Mettre à jour la conversation dans la liste
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? {...c, dernier_message: newMessage.substring(0, 100), dernier_message_date: new Date().toISOString()}
          : c
      ));
      
      inputRef.current?.focus();
    } catch (error) {
      console.error('Erreur envoi message:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async (contact) => {
    try {
      const response = await api.post(`/messagerie/conversations?destinataire_id=${contact.id}`);
      setShowNewConversation(false);
      
      // Ajouter ou mettre à jour la conversation dans la liste
      const existingIndex = conversations.findIndex(c => c.id === response.data.id);
      if (existingIndex === -1) {
        setConversations(prev => [response.data, ...prev]);
      }
      
      // Ouvrir la conversation
      loadMessages(response.data.id);
    } catch (error) {
      console.error('Erreur création conversation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const getOtherParticipant = (conversation) => {
    if (!conversation?.participants_info) return { nom: 'Inconnu', role: '' };
    return conversation.participants_info.find(p => p.id !== currentUserId) || { nom: 'Inconnu', role: '' };
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>;
      case 'referent':
        return <Badge className="bg-blue-100 text-blue-700 text-xs">Référent</Badge>;
      case 'animateur':
        return <Badge className="bg-emerald-100 text-emerald-700 text-xs">Intervenant</Badge>;
      default:
        return null;
    }
  };

  const getInitials = (nom) => {
    if (!nom) return '?';
    return nom.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const filteredContacts = contacts.filter(c => 
    `${c.prenom} ${c.nom}`.toLowerCase().includes(searchContact.toLowerCase()) ||
    c.email.toLowerCase().includes(searchContact.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 rounded-3xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2 text-blue-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <MessageCircle className="w-6 h-6" />
            Messagerie Interne
          </CardTitle>
          <p className="text-blue-700 text-sm">
            Échangez avec les Administrateurs, Référents et Intervenants.
          </p>
        </CardHeader>
      </Card>

      {/* Interface de messagerie */}
      <Card className="bg-white rounded-3xl border-slate-200 overflow-hidden">
        <div className="flex h-[600px]">
          {/* Liste des conversations */}
          <div className={`w-full md:w-1/3 border-r border-slate-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {/* Header liste */}
            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800">Conversations</h3>
                <Button 
                  size="sm" 
                  onClick={() => setShowNewConversation(true)}
                  className="rounded-xl bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Liste */}
            <ScrollArea className="flex-1">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune conversation</p>
                  <Button 
                    variant="link" 
                    onClick={() => setShowNewConversation(true)}
                    className="text-blue-600"
                  >
                    Démarrer une conversation
                  </Button>
                </div>
              ) : (
                conversations.map((conv) => {
                  const other = getOtherParticipant(conv);
                  return (
                    <div
                      key={conv.id}
                      onClick={() => loadMessages(conv.id)}
                      className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-blue-100 text-blue-700">
                            {getInitials(other.nom)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-slate-800 truncate">{other.nom}</span>
                            {conv.non_lus > 0 && (
                              <Badge className="bg-blue-600 text-white text-xs">{conv.non_lus}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getRoleBadge(other.role)}
                          </div>
                          {conv.dernier_message && (
                            <p className="text-sm text-slate-500 truncate mt-1">{conv.dernier_message}</p>
                          )}
                          {conv.dernier_message_date && (
                            <p className="text-xs text-slate-400 mt-1">{formatDate(conv.dernier_message_date)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </ScrollArea>
          </div>

          {/* Zone de conversation */}
          <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {selectedConversation ? (
              <>
                {/* Header conversation */}
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="md:hidden"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-blue-100 text-blue-700">
                      {getInitials(getOtherParticipant(selectedConversation).nom)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-800">{getOtherParticipant(selectedConversation).nom}</h3>
                    {getRoleBadge(getOtherParticipant(selectedConversation).role)}
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      <p>Aucun message. Commencez la conversation !</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => {
                        const isMe = msg.expediteur_id === currentUserId;
                        return (
                          <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] ${isMe ? 'order-2' : ''}`}>
                              <div className={`rounded-2xl px-4 py-2 ${
                                isMe 
                                  ? 'bg-blue-600 text-white rounded-br-sm' 
                                  : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                              }`}>
                                <p className="whitespace-pre-wrap break-words">{msg.contenu}</p>
                              </div>
                              <div className={`flex items-center gap-1 mt-1 text-xs text-slate-400 ${isMe ? 'justify-end' : ''}`}>
                                <span>{formatDate(msg.created_at)}</span>
                                {isMe && msg.lu && <CheckCheck className="w-3 h-3 text-blue-500" />}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 bg-slate-50">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrivez votre message..."
                      className="flex-1 rounded-xl"
                      disabled={sending}
                    />
                    <Button 
                      type="submit" 
                      disabled={!newMessage.trim() || sending}
                      className="rounded-xl bg-blue-600 hover:bg-blue-700"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-medium">Sélectionnez une conversation</p>
                  <p className="text-sm">ou démarrez-en une nouvelle</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Dialog nouvelle conversation */}
      <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Nouvelle conversation
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un contact pour démarrer une conversation.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchContact}
                onChange={(e) => setSearchContact(e.target.value)}
                placeholder="Rechercher un contact..."
                className="pl-10 rounded-xl"
              />
            </div>

            <ScrollArea className="h-64">
              {filteredContacts.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Aucun contact trouvé</p>
              ) : (
                <div className="space-y-2">
                  {filteredContacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => startNewConversation(contact)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-blue-100 text-blue-700">
                          {getInitials(`${contact.prenom} ${contact.nom}`)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{contact.prenom} {contact.nom}</p>
                        <div className="flex items-center gap-2">
                          {getRoleBadge(contact.role)}
                          <span className="text-xs text-slate-400">{contact.email}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messagerie;
